import {Style} from "interfaces";
import {File} from "react-feather";
import {AbsoluteIcon} from "./lib";
import {useAppDispatch} from "hooks";
import {showAlert} from "global/slices";
import {useTheme} from "styled-components";
import {typographyErrorProps} from "../Input/styled";
import {AddPhotoAlternate} from "@mui/icons-material";
import {Column, Typography} from "components/@system";
import {Container, Image, Media, Video} from "./styled";
import {memo, useEffect, useRef, useState} from "react";
import {checkVideoFormat, checkImageFormat} from "utils";
import {
  MAX_IMAGE_MB_SIZE,
  MAX_VIDEO_MB_SIZE,
  MAX_VIDEO_MB_ERROR_MESSAGE,
  MAX_IMAGE_MB_ERROR_MESSAGE,
} from "constants/max-file-size";

interface MultimediaManagerProps extends Style {
  label?: string;
  media: string[];
  multiple?: boolean;
  helperErrorText?: string;
  type?: "view-only" | "editable";
  onRemove: (url: string) => void;
  extension: "video" | "image" | "all";
  onNewMedia?: (files: File[]) => void;
}

export const MultimediaManager: React.FC<MultimediaManagerProps> = memo(
  ({
    media,
    label,
    style,
    onRemove,
    extension,
    onNewMedia,
    helperErrorText,
    multiple = true,
    type = "editable",
  }) => {
    const dispatch = useAppDispatch();
    const [files, setFiles] = useState<File[]>([]);
    const {black05, white05, blackWhite, isDark} = useTheme();

    let isEditable = type === "editable";
    const inputFile = useRef<HTMLInputElement>(null);
    const videoOnly = "video/*";
    const imageOnly = "image/*";
    const all = "";
    const MAX_SIZE =
      extension === "image" ? MAX_IMAGE_MB_SIZE : MAX_VIDEO_MB_SIZE;
    const MAX_SIZE_ERROR_MESSAGE =
      extension === "image"
        ? MAX_IMAGE_MB_ERROR_MESSAGE
        : MAX_VIDEO_MB_ERROR_MESSAGE;

    if (!multiple) {
      isEditable = media["length"] === 0;
    }

    useEffect(() => {
      if (onNewMedia && !!files["length"]) onNewMedia(files);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    return (
      <Column gap={label ? "5px" : 0} {...style}>
        {label && <Typography fontWeight="500">{label}</Typography>}
        <Container>
          <input
            id="file"
            type="file"
            ref={inputFile}
            style={{display: "none"}}
            accept={
              extension === "all"
                ? all
                : extension === "image"
                ? imageOnly
                : videoOnly
            }
            onChange={({target}) => {
              const {files} = target;
              if (files) {
                const file = files[0];

                if (file.size > MAX_SIZE) {
                  dispatch(
                    showAlert({type: "error", title: MAX_SIZE_ERROR_MESSAGE})
                  );
                  return;
                }
                setFiles((prev) => [...prev, file]);
              }
            }}
          />
          {media.map((src, key) => {
            const metadata =
              typeof src !== "string" ? (src as File) : undefined;
            src = typeof src === "string" ? src : URL.createObjectURL(src);
            const isVideo = checkVideoFormat(metadata ? metadata["name"] : src);
            const isImage = checkImageFormat(metadata ? metadata["name"] : src);

            return (
              <Media
                key={key}
                style={{width: isVideo ? 140 : 90, height: isVideo ? 150 : 90}}
              >
                {isVideo ? (
                  <Video controls src={src} />
                ) : isImage ? (
                  <Image src={src} />
                ) : (
                  <Column alignItems="center" gap={5}>
                    <File color={blackWhite} size={20} />
                    <Typography
                      width="100%"
                      fontSize={12}
                      textAlign="center"
                      className="line-clamp-2"
                    >
                      {metadata ? metadata["name"] : "Archivo"}
                    </Typography>
                  </Column>
                )}
                <AbsoluteIcon
                  onClick={() => {
                    onRemove(src);
                    setFiles([]);
                  }}
                />
              </Media>
            );
          })}
          {isEditable && (
            <Media
              onClick={() => {
                if (inputFile["current"]) inputFile["current"]["click"]();
              }}
            >
              <AddPhotoAlternate
                style={{fontSize: 40, color: isDark ? white05 : black05}}
              />
            </Media>
          )}
        </Container>
        {helperErrorText && (
          <Typography {...typographyErrorProps} marginTop={0} textAlign="start">
            {helperErrorText}
          </Typography>
        )}
      </Column>
    );
  },
  (prev, next) =>
    JSON.stringify(prev["media"]) === JSON.stringify(next["media"])
);
